// client/src/dnd/emitter_tool/engine/SpriteManager.js
import * as PIXI from "pixi.js";
import { buildSpriteSource } from "./engine_parts/sprites"; // ← uses Single/Multi/Anim sources

export const MODE_KEY        = "EmitterTool:SpritePanel:ModeV1";            // 'single' | 'multi' | 'anim'
export const LOCAL_MULTI_KEY = "EmitterTool:SpritePanel:LocalMultiV1";      // JSON string array of names
export const LOCAL_ANIM_KEY  = "EmitterTool:SpritePanel:LocalAnimFramesV1"; // JSON string array of names
export const ANIM_FPS_KEY    = "EmitterTool:SpritePanel:AnimFPSV1";         // number

// ---- Optional UI events (emit these from your SpritePanel if you want real-time updates) ----
// window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetMode",   { detail: { mode } }))
// window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetMulti",  { detail: { names } }))
// window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetAnim",   { detail: { frames, fps } }))
// window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetSingle", { detail: { name } }))
const EVT_SET_MODE   = "EmitterTool:SpritePanel:SetMode";
const EVT_SET_MULTI  = "EmitterTool:SpritePanel:SetMulti";
const EVT_SET_ANIM   = "EmitterTool:SpritePanel:SetAnim";
const EVT_SET_SINGLE = "EmitterTool:SpritePanel:SetSingle";

// ---- Helpers ----
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function isTextureMap(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const vals = Object.values(obj);
  return vals.length > 0 && vals.every(
    (v) => v && typeof v === "object" && ("baseTexture" in v || "valid" in v)
  );
}

function safeGetJSON(key, fallback = []) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}
function safeGetNumber(key, fallback = 12) {
  try {
    const raw = localStorage.getItem(key);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}
function safeGetMode(key, fallback = "single") {
  try {
    const m = (localStorage.getItem(key) || "").toLowerCase();
    return m === "single" || m === "multi" || m === "anim" ? m : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}
function writeNumber(key, value) {
  try { localStorage.setItem(key, String(value)); } catch {}
}
function writeMode(key, value) {
  try { localStorage.setItem(key, String(value)); } catch {}
}

// ---- Name resolution (panel names → atlas keys) ----
const norm = (s) => String(s || "").trim().toLowerCase();
const baseNoExt = (str) => {
  const slash = Math.max(str.lastIndexOf("/"), str.lastIndexOf("\\"));
  const base = slash >= 0 ? str.slice(slash + 1) : str;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
};
function resolveKey(keys, req) {
  const low = norm(req);
  return (
    keys.find((k) => norm(k) === low) ||                          // exact
    keys.find((k) => baseNoExt(k).toLowerCase() === low) ||       // basename
    keys.find((k) => norm(k).includes(low)) ||                    // substring
    null
  );
}
function resolveListToKeys(textures, names) {
  const keys = Object.keys(textures || {});
  const out = [];
  const seen = new Set();
  for (const req of names || []) {
    if (!req) continue;
    const k = resolveKey(keys, req);
    if (k && !seen.has(k)) { seen.add(k); out.push(k); }
  }
  return out;
}

/**
 * SpriteManager
 * Holds "selection" (mode + lists) and exposes a stateless source { next(dt): Texture }.
 * **Exclusive mode**: single vs multi vs anim are mutually exclusive (no cross fallbacks).
 */
export default class SpriteManager {
  /**
   * @param {Object} opts
   * @param {Record<string, PIXI.Texture>} opts.textures
   * @param {boolean} [opts.readFromStorage=true]  read initial selection from localStorage
   * @param {boolean} [opts.writeToStorage=true]   persist updates to localStorage
   * @param {boolean} [opts.listenUIEvents=true]   listen for optional UI events
   */
  constructor({ textures = {}, readFromStorage = true, writeToStorage = true, listenUIEvents = true } = {}) {
    this._textures = isTextureMap(textures) ? textures : {};
    this._write = !!writeToStorage;

    // internal selection state (mirrors storage)
    this._selection = {
      mode: "single",      // 'single' | 'multi' | 'anim'
      single: null,        // string
      multi: [],           // string[]
      anim: [],            // string[]
      animFps: 12,         // number
    };

    if (readFromStorage) {
      this.refreshFromStorage();
    } else {
      // sensible fallback if nothing set
      const names = Object.keys(this._textures);
      if (names.length && !this._selection.single) this._selection.single = names[0];
    }

    this._rebuildSource();

    if (listenUIEvents) this._attachUIEvents();
  }

  // ---------- public API ----------
  getTextures() { return this._textures; }
  getTextureNames() { return Object.keys(this._textures || {}); }

  setTextures(textures) {
    this._textures = isTextureMap(textures) ? textures : {};
    // keep raw strings in selection; resolution happens inside the source
    this._rebuildSource();
  }

  /** Returns a stateless object { next(dt): PIXI.Texture } */
  getSpriteSource() {
    return this._spriteSource;
  }

  /** A minimal payload the engine can consume if it prefers selection over source */
  getSelectionForEngine() {
    // Strict exclusivity: only the active bucket is filled; others empty
    const m = this._selection.mode;
    return {
      mode: m,
      single: m === "single" ? this._selection.single : null,
      multi:  m === "multi"  ? this._selection.multi.slice() : [],
      anim:   m === "anim"   ? this._selection.anim.slice()  : [],
      animFps: clamp(Math.floor(this._selection.animFps || 12), 1, 120),
    };
  }

  /** Optional helper if you want to pass a ready-to-use source to the engine */
  getSourceForEngine() {
    return this._spriteSource;
  }

  getSelection() {
    return JSON.parse(JSON.stringify(this._selection));
  }

  // --- setters (also persist when enabled) ---
  setMode(mode) {
    const m = String(mode || "").toLowerCase();
    if (m !== "single" && m !== "multi" && m !== "anim") return;
    this._selection.mode = m;
    if (this._write) writeMode(MODE_KEY, m);
    this._rebuildSource();
  }

  setSingle(name) {
    if (typeof name !== "string" || !name) return;
    this._selection.single = name;
    // note: single name is not persisted by the panel; staying in-memory matches previous behavior
    this._rebuildSource();
  }

  setMulti(names) {
    const valid = Array.isArray(names) ? names.filter((n) => typeof n === "string") : [];
    this._selection.multi = valid;
    if (this._write) writeJSON(LOCAL_MULTI_KEY, valid);
    this._rebuildSource();
  }

  setAnim(frames, fps = 12) {
    const valid = Array.isArray(frames) ? frames.filter((n) => typeof n === "string") : [];
    this._selection.anim = valid;
    this._selection.animFps = clamp(Math.floor(fps || 12), 1, 120);
    if (this._write) {
      writeJSON(LOCAL_ANIM_KEY, valid);
      writeNumber(ANIM_FPS_KEY, this._selection.animFps);
    }
    this._rebuildSource();
  }

  /** Pull the latest panel state from localStorage */
  refreshFromStorage() {
    const names = this.getTextureNames();
    const mode = safeGetMode(MODE_KEY, "single");
    const multi = safeGetJSON(LOCAL_MULTI_KEY, []);
    const anim  = safeGetJSON(LOCAL_ANIM_KEY,  []);
    const animFps = clamp(Math.floor(safeGetNumber(ANIM_FPS_KEY, 12)), 1, 120);

    // choose a single fallback for convenience if not set
    let single = this._selection.single;
    if (!single) {
      const resolvedMulti = resolveListToKeys(this._textures, multi);
      const resolvedAnim  = resolveListToKeys(this._textures, anim);
      if (mode === "multi" && resolvedMulti.length) single = resolvedMulti[0];
      else if (mode === "anim" && resolvedAnim.length) single = resolvedAnim[0];
      else single = names[0] || null;
    }

    this._selection = { mode, single, multi, anim, animFps };
    this._rebuildSource();
  }

  destroy() {
    this._detachUIEvents();
    this._textures = {};
    this._spriteSource = { next: () => PIXI.Texture.WHITE };
  }

  // ---------- internals ----------
  _rebuildSource() {
    // Build **exclusive** source: only the active list is honored
    const sel = this.getSelectionForEngine(); // collapses others to empty
    this._spriteSource = buildSpriteSource(this._textures, sel);
  }

  _attachUIEvents() {
    if (this._evtAttached) return;
    this._evtAttached = true;

    this._onSetMode = (e) => {
      const m = String(e?.detail?.mode || "").toLowerCase();
      if (!m) return;
      this.setMode(m);
    };

    this._onSetMulti = (e) => {
      const names = Array.isArray(e?.detail?.names) ? e.detail.names : [];
      this.setMulti(names);
    };

    this._onSetAnim = (e) => {
      const frames = Array.isArray(e?.detail?.frames) ? e.detail.frames : [];
      const fps = Number(e?.detail?.fps);
      this.setAnim(frames, Number.isFinite(fps) ? fps : this._selection.animFps);
    };

    this._onSetSingle = (e) => {
      const name = e?.detail?.name;
      if (typeof name === "string") this.setSingle(name);
    };

    window.addEventListener(EVT_SET_MODE, this._onSetMode);
    window.addEventListener(EVT_SET_MULTI, this._onSetMulti);
    window.addEventListener(EVT_SET_ANIM, this._onSetAnim);
    window.addEventListener(EVT_SET_SINGLE, this._onSetSingle);
  }

  _detachUIEvents() {
    if (!this._evtAttached) return;
    this._evtAttached = false;
    window.removeEventListener(EVT_SET_MODE, this._onSetMode);
    window.removeEventListener(EVT_SET_MULTI, this._onSetMulti);
    window.removeEventListener(EVT_SET_ANIM, this._onSetAnim);
    window.removeEventListener(EVT_SET_SINGLE, this._onSetSingle);
  }
}
